/**
 * Agent SSE 客户端（对齐 aichatopenrouter agent-client）
 * 事件：text_delta | tool_start | tool_complete | tool_end | todos |
 * approval_request | turn | usage | card | done | error
 */
import { resolveApiBaseURL } from './appBase'

const IDLE_TIMEOUT_MS = 180_000

/**
 * @param {string} path API 路径，如 /api/agent/chat/stream
 * @param {object} body JSON body
 * @param {object} callbacks
 * @param {{ signal?: AbortSignal, headers?: object }} [options]
 */
export async function runAgentStream(path, body, callbacks = {}, options = {}) {
  const url = `${resolveApiBaseURL()}${path.startsWith('/') ? path : `/${path}`}`
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(options.headers || {}),
    },
    body: JSON.stringify(body || {}),
    signal: options.signal,
  })

  if (!resp.ok) {
    let detail = resp.statusText
    try {
      const err = await resp.json()
      detail = err.detail || err.error || detail
    } catch (_) {
      /* ignore */
    }
    throw new Error(typeof detail === 'string' ? detail : `HTTP ${resp.status}`)
  }

  const reader = resp.body?.getReader()
  if (!reader) throw new Error('无响应流')

  const decoder = new TextDecoder()
  let buffer = ''
  const tools = new Map()
  let fullText = ''
  let receivedTerminal = false

  try {
    while (true) {
      let idleTimer
      const readResult = await Promise.race([
        reader.read(),
        new Promise((_, reject) => {
          idleTimer = setTimeout(
            () => reject(new Error('长时间无响应，请重试或检查后端任务是否卡住')),
            IDLE_TIMEOUT_MS,
          )
        }),
      ]).finally(() => {
        if (idleTimer) clearTimeout(idleTimer)
      })

      const { done, value } = readResult
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const raw of lines) {
        const line = raw.trim()
        if (!line.startsWith('data:')) continue
        const data = line.slice(5).trim()
        if (!data) continue
        if (data === '[DONE]') {
          receivedTerminal = true
          continue
        }

        let ev
        try {
          ev = JSON.parse(data)
        } catch {
          continue
        }

        callbacks.onEvent?.(ev)
        const type = ev.type || ''

        if (type === 'text_delta') {
          const chunk = ev.content || ''
          fullText += chunk
          callbacks.onTextDelta?.(chunk)
        } else if (type === 'tool_start') {
          const tool = {
            id: ev.id || ev.toolCallId,
            name: ev.name || ev.toolName,
            label: ev.label || ev.name || ev.toolName,
            args: ev.args || {},
            status: 'running',
            startedAt: ev.startedAt || Date.now(),
            endedAt: null,
            summary: '',
            result: null,
          }
          tools.set(tool.id, tool)
          callbacks.onToolStart?.(tool)
        } else if (type === 'tool_complete' || type === 'tool_end') {
          const tid = ev.id || ev.toolCallId
          const prev = tools.get(tid) || {
            id: tid,
            name: ev.name,
            label: ev.label || ev.name,
            args: {},
            startedAt: Date.now(),
          }
          const tool = {
            ...prev,
            status: ev.status === 'error' || ev.isError ? 'error' : 'success',
            summary: ev.summary || (typeof ev.output === 'string' ? ev.output.slice(0, 120) : ''),
            result: ev.result ?? ev.output,
            endedAt: ev.endedAt || Date.now(),
          }
          tools.set(tool.id, tool)
          callbacks.onToolComplete?.(tool)
        } else if (type === 'todos') {
          callbacks.onTodos?.(ev.items || [])
        } else if (type === 'approval_request') {
          callbacks.onApprovalRequest?.(ev)
        } else if (type === 'turn') {
          callbacks.onTurn?.(ev)
        } else if (type === 'usage') {
          callbacks.onUsage?.(ev)
        } else if (type === 'run_meta') {
          callbacks.onRunMeta?.(ev)
        } else if (type === 'card') {
          callbacks.onCard?.(ev.card_type, ev.data)
        } else if (type === 'done') {
          receivedTerminal = true
          callbacks.onComplete?.(ev.result || {})
        } else if (type === 'error') {
          receivedTerminal = true
          callbacks.onError?.(new Error(ev.error || '未知错误'))
        }
      }
    }
  } catch (e) {
    if (e?.name === 'AbortError') throw e
    callbacks.onError?.(e instanceof Error ? e : new Error(String(e)))
    throw e
  } finally {
    try {
      reader.releaseLock()
    } catch (_) {
      /* ignore */
    }
  }

  if (!receivedTerminal) {
    callbacks.onComplete?.({ response: fullText, status: 'completed' })
  }

  return {
    text: fullText,
    tools: [...tools.values()],
  }
}

/** 把流事件累积成消息 parts（交错 text / tool / approval） */
export function createPartsAccumulator() {
  /** @type {Array<object>} */
  const parts = []

  return {
    parts,
    onTextDelta(content) {
      const last = parts[parts.length - 1]
      if (last?.kind === 'text') {
        last.content += content
      } else {
        parts.push({ kind: 'text', content })
      }
    },
    onToolStart(tool) {
      parts.push({ kind: 'tool', id: tool.id, tool: { ...tool } })
    },
    onToolComplete(tool) {
      const idx = parts.findIndex((p) => p.kind === 'tool' && p.id === tool.id)
      if (idx >= 0) {
        parts[idx].tool = { ...tool }
      } else {
        parts.push({ kind: 'tool', id: tool.id, tool: { ...tool } })
      }
    },
    onApprovalRequest(ev) {
      parts.push({
        kind: 'approval',
        approvalId: ev.approvalId,
        toolCallId: ev.toolCallId,
        name: ev.name,
        label: ev.label || ev.name,
        args: ev.args || {},
        summary: ev.summary || '',
        state: 'pending',
      })
    },
  }
}
